import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { RangePickerValue } from '../components/reports/range-picker.js';
import { ReportShell, StatTile } from '../components/reports/report-shell.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useHasModule } from '../lib/billing/entitlements.js';
import { formatCurrency, formatNumber } from '../lib/format.js';
import { staffReportCsvPath } from '../lib/reports/reports-api.js';
import { useStaffReport } from '../lib/reports/use-reports.js';

/**
 * Who finished what, how quickly, and what it earned them.
 *
 * This is the ONE report a karigar can open, and what they get is their own row — the API pins
 * the query to them, so this screen renders whatever it is given rather than filtering anything
 * itself. When it is their own figures, the page says so: a productivity table that looks like
 * the whole shop's but silently contains one person would be a confusing thing to be shown.
 *
 * Earnings here are the frozen rate each job was handed over at, which is why they reconcile
 * exactly with the payout screen rather than approximately.
 */
const DAY_MS = 86_400_000;

export function StaffReportPage(): ReactElement {
  const { t } = useTranslation();
  const [range, setRange] = useState<RangePickerValue>({ preset: 'last_30_days' });

  const { role } = useAuth();
  // A karigar's own row is part of the karigar floor; the whole shop's table is what Pro sells.
  // The API draws the same line by permission scope, which the matrix pins to the tailor role.
  const module = role === 'tailor' ? 'workforce' : 'advanced_analytics';
  const inPlan = useHasModule(module);
  const report = useStaffReport(range, { enabled: inPlan });
  const data = report.data;

  return (
    <ReportShell
      title={t('reports.staff.title')}
      description={t('reports.staff.description')}
      range={range}
      onRangeChange={setRange}
      resolved={data?.range}
      csvPath={staffReportCsvPath(range)}
      isPending={report.isPending && inPlan}
      module={module}
    >
      {data ? (
        <>
          {data.ownScope ? (
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm" role="status">
              {t('reports.staff.ownScopeNote')}
            </p>
          ) : null}

          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile
              label={t('reports.staff.jobsCompleted')}
              value={formatNumber(data.totals.jobsCompleted)}
              hint={t('reports.staff.garments', { count: data.totals.quantity })}
            />
            <StatTile
              label={t('reports.staff.earned')}
              value={formatCurrency(data.totals.earnedPaise)}
            />
            <StatTile
              label={t('reports.staff.settled')}
              value={formatCurrency(data.totals.settledPaise)}
            />
            <StatTile
              label={t('reports.staff.unsettled')}
              value={formatCurrency(data.totals.unsettledPaise)}
            />
          </dl>

          <section className="flex flex-col gap-2" aria-label={t('reports.staff.perPerson')}>
            <h2 className="text-sm font-semibold">{t('reports.staff.perPerson')}</h2>
            {data.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.noData')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th scope="col" className="py-1 font-medium">
                        {t('reports.staff.name')}
                      </th>
                      <th scope="col" className="py-1 text-right font-medium">
                        {t('reports.staff.jobs')}
                      </th>
                      <th scope="col" className="py-1 text-right font-medium">
                        {t('reports.staff.turnaround')}
                      </th>
                      <th scope="col" className="py-1 text-right font-medium">
                        {t('reports.staff.reworks')}
                      </th>
                      <th scope="col" className="py-1 text-right font-medium">
                        {t('reports.staff.earned')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={row.staffUserId} className="border-t border-border">
                        <th scope="row" className="py-1.5 text-left font-normal">
                          {row.staffName}
                        </th>
                        <td className="py-1.5 text-right tabular-nums">
                          {formatNumber(row.jobsCompleted)}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {row.averageTurnaroundMs === null
                            ? '—'
                            : (row.averageTurnaroundMs / DAY_MS).toFixed(1)}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {formatNumber(row.reworks)}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {formatCurrency(row.earnedPaise)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </ReportShell>
  );
}

export default StaffReportPage;
